import {
	memo,
	ReactElement,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { DateTime } from 'luxon';
import { useNavigate, useRouter } from '@tanstack/react-router';

import config from '@/config';
import { Skeleton } from '@shared/ui/skeleton';

import styles from './top-stories.module.css';
import {
	useFeed,
	Item,
	useRevolveAd,
	feedItemTransitionName,
} from './top-stories.hooks';
import { useViewportFillingCount } from '../use-viewport-filling-count';
import { T, useT } from '@shared/i18n';

const FEED_URL = 'https://www.pw.org/articles/feed';

const DATE_FORMAT = 'MMMM dd, yyyy';

export const TopStories = () => {
	const t = useT();
	const navigate = useNavigate();
	const [feedUrl] = useState<string>(FEED_URL);

	const { feed, isLoading } = useFeed(feedUrl);
	const { title } = config.build;

	// `replace: true` is load-bearing, not a style preference: this effect runs
	// on every mount of this section, including landing back here via the
	// browser's own back button. Without it each of those mounts *pushed* a
	// fresh history entry rather than updating the current one, so pressing
	// back walked through the synthetic entries this effect kept adding
	// instead of the reader's actual navigation. Matching morningstar's
	// Phase 0.12 fix, which had one such effect; poets has four.
	useEffect(() => {
		navigate({
			to: '/',
			search: (prev: Record<string, unknown>) => ({
				...prev,
				section: 'top-stories',
			}),
			replace: true,
		});
	}, [navigate]);
	const classNames = [styles['feed-container']];
	if (isLoading) {
		classNames.push(styles['loading']);
	}

	// The feed rows plus their interleaved ad banners were rebuilt -- every
	// element, every render -- in the JSX below. `feed` only changes when the
	// RSS query resolves, so everything downstream re-rendered for nothing.
	const feedItems = useMemo(
		() =>
			feed
				.map((item: Item, i: number) => <FeedItem key={i} {...item} />)
				.reduce(reduceBanner, [] as Array<ReactElement>),
		[feed],
	);
	return (
		<section className={styles['top-stories']}>
			<div className={classNames.join(' ')}>
				{isLoading ? (
					<FeedSkeleton />
				) : (
					<>
						<h1>{t('template.poets.latest')}</h1>
						<h2>
							<T
								k="template.poets.latestIntro"
								values={{ title }}
								components={{
									em: (chunks) => <em>{chunks}</em>,
									br: () => <br />,
								}}
							/>
						</h2>
						<div className={styles['feed']}>{feedItems}</div>
						<footer>
<T
						k="template.poets.discoverMore"
						components={{
							link: (chunks) => (
								<a href="https://www.pw.org" rel="noreferrer" target="_blank">
									{chunks}
								</a>
							),
						}}
					/>
						</footer>
					</>
				)}
			</div>
		</section>
	);
};

function FeedSkeleton() {
	const { containerRef, count } = useViewportFillingCount();
	return (
		<div className={styles['feed-container']} aria-busy="true">
			<Skeleton className="h-7 w-40 mt-5 md:mt-7.5 mb-2.5 mx-auto bg-gray-300" />
			<Skeleton className="h-5 w-[90%] max-w-5xl mx-auto mb-2.5 md:mb-12.5 sm:mb-5 bg-gray-300" />
			<div className={styles['feed']} ref={containerRef}>
				{Array.from({ length: count }).map((_, i) => (
					<div
						key={i}
						className={`${styles['feed-item-container']} mb-2`}
					>
						<div className={styles['feed-item']}>
							<div className={styles['image']}>
								<Skeleton className="w-full h-full rounded-none bg-gray-300" />
							</div>
							<div className={styles['title']}>
								<Skeleton className="h-4 w-4/5 mt-2.5 bg-gray-300" />
							</div>
							<div className={styles['content']}>
								<Skeleton className="h-3 w-24 bg-gray-300" />
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

function reduceBanner(
	acc: Array<ReactElement>,
	item: ReactElement,
	i: number,
): Array<ReactElement> {
	acc.push(item);
	if (i === 2) {
		acc.push(
			<Banner
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={65}
				key={`${i}-ad`}
				ad="ADVERTISEMENT"
			/>,
		);
	}
	if (i === 5) {
		acc.push(
			<Banner
				id="fbf717dc1969dc79e517b3994cd0df44"
				zone={64}
				key={`${i}-ad`}
				ad="ADVERTISEMENT"
			/>,
		);
	}

	return acc;
}

function Banner({ id, zone, ad }: { id: string; zone: number; ad?: string }) {
	const ref = useRef<HTMLDivElement>(null);
	useRevolveAd(ref);
	return (
		<div ref={ref} className={styles['banner']}>
			<div>
				<div className={styles['ins-wrapper']}>
					<ins data-revive-zoneid={zone} data-revive-id={id}></ins>
				</div>
				{ref && ad ? <div className={styles['ad']}>{ad}</div> : null}
			</div>
		</div>
	);
}

export const Stories = ({ stories }: StoriesProps) => {
	return (
		<div className={styles['stories']}>
			<div className={styles['feed-container']}>
				{stories.map((story) => (
					<Story key={story.id} {...story} />
				))}
			</div>
		</div>
	);
};

interface StoriesProps {
	stories: Array<StoryProps>;
}

// Memoized: one row per feed entry, and the whole list re-rendered on any
// parent tick before this.
const FeedItem = memo(function FeedItem(item: Item) {
	const t = useT();
	const router = useRouter();
	const { title, category, pubDate, thumbnail, link } = item;
	// Only applied when there is actually a thumbnail to morph -- feed.tsx's
	// Content guards its own <img> behind the identical `thumbnail ?` check,
	// so a null thumbnail here has no counterpart to matter against anyway.
	const transitionName =
		thumbnail && link ? feedItemTransitionName(link) : undefined;

	const dateTime = DateTime.fromJSDate(pubDate);

	const onClick = () => {
		router.navigate({
			to: '/feed',
			replace: true,
			params: {},
			// Cast: TanStack Router's route-state type is inferred from the
			// route tree and doesn't know about this ad-hoc `{item,
			// section}` shape -- same reasoning as elsewhere `as unknown as`
			// bridges a value against a type the router can't derive here.
			state: { item, section: 'top-stories' } as unknown as never,
			// Continuity comes from the shared Topbar's fixed
			// `view-transition-name: topbar-nav` (topbar.module.css), which
			// both home and /feed carry -- same mechanism as every other
			// `viewTransition` usage in this codebase. `router.navigate`
			// (imperative, used here since the whole row is the click
			// target rather than a <Link>) takes the identical option.
			viewTransition: true,
		});
	};

	const subject = [];

	subject.push(dateTime.toFormat(DATE_FORMAT));

	return (
		<div className={styles['feed-item-container']}>
			<div
				// eslint-disable-next-line jsx-a11y/prefer-tag-over-role
				role="button"
				tabIndex={0}
				className={styles['feed-item']}
				title={title ?? undefined}
				onClick={onClick}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') onClick();
				}}
			>
				<div className={styles['image']}>
					{thumbnail ? (
						<img
							loading="lazy"
							src={thumbnail}
							alt={t('common.thumbnail')}
							style={
								transitionName
									? { viewTransitionName: transitionName }
									: undefined
							}
						/>
					) : (
						<div />
					)}
				</div>
				<div className={styles['title']}>
					{category ? <h4>{category}</h4> : null}
					<h3 dir="auto">{title}</h3>
				</div>
				<div className={styles['content']}>
					<small>{subject.join(' - ')}</small>
				</div>
			</div>
		</div>
	);
});

export const Story = memo(function Story({
	title,
	thumbnail,
	tags,
	excerpt,
	url,
}: StoryProps) {
	const t = useT();
	let tagsComponent: Array<ReactElement> | null = null;
	if (tags) {
		tagsComponent = tags.map((tag) => <div key={tag}>{tag}</div>);
	}
	return (
		<div className={styles['story']}>
			<a
				className={styles['image']}
				href={url}
				target="_blank"
				rel="noreferrer"
			>
				<img loading="lazy" src={thumbnail} alt={t('common.coverOf', { name: title })} />
			</a>

			<h4>
				<a dir="auto" href={url} target="_blank" rel="noreferrer">
					{title}
				</a>
			</h4>
			{tagsComponent?.length ? (
				<div className={styles['tags']}>{tagsComponent}</div>
			) : null}

			{excerpt ? <p>{excerpt}</p> : null}
		</div>
	);
});

export const Offer = ({ title, thumbnail, url }: StoryProps) => {
	return (
		<div className={styles['offer']}>
			<a href={url} target="_blank" rel="noreferrer">
				<img loading="lazy" src={thumbnail} alt={title} />
			</a>
		</div>
	);
};

interface StoryProps {
	id: string;
	title: string;
	thumbnail?: string;
	url?: string;
	tags?: Array<string>;
	excerpt?: string;
	isOffer?: boolean;
}
